import { FoxIconCheck, FoxIconCopyBold2 } from 'aui-biz-icons';
import classNames from 'clsx';
// eslint-disable-next-line you-dont-need-lodash-underscore/is-string
import isString from 'lodash-es/isString';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { MarkdownSafeMdxViewerLocaleContext } from '../MarkdownSafeMdxViewer/MarkdownSafeMdxViewerLocaleContext';
import { copyTo } from '../utils/clipboard';
import { formatJSONObjectString } from './formatJSONObjectString';
import './highlightjs/github-dark.less';
import './highlightjs/google-light.less';
import styles from './index.module.less';
import {
  hasRegisteredLanguage,
  highlightCodeByLanguage,
  loadLang,
  transformLanguage,
  updateHighLightCode,
} from './LangManager';
// import { highLightInWorker } from './LangManagerWorkerEntry';
import { ClientOnly } from 'remix-utils/client-only';
import { formatMessageWithLocale } from '../../locales';

export { formatJSONObjectString };

const highLightLimitSize = 100000;

const CodeBlock = ({
  code,
  language = 'json',
  highlightTheme,
  theme,
  onlyCode = true,
  className,
}: {
  code: string;
  language?: string;
  theme?: string;
  highlightTheme?: string;
  onlyCode?: boolean;
  className?: string;
}) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<any>(null);
  const lang = transformLanguage(language);
  const locale = React.useContext(MarkdownSafeMdxViewerLocaleContext);
  const highLightRef = useRef(true);
  const preHighlightCodeLangRef = React.useRef<
    | {
        code: string;
        lang: string;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    loadLang(lang);
  }, [lang]);

  const [highLightValue, setHighLightCode] = useState(() => {
    if (code.length > highLightLimitSize) {
      preHighlightCodeLangRef.current = undefined;
      highLightRef.current = false;
      return code;
    } else {
      const hasRegistered = hasRegisteredLanguage(lang);
      if (hasRegistered) {
        highLightRef.current = true;
        preHighlightCodeLangRef.current = {
          lang,
          code,
        };
        return highlightCodeByLanguage(lang, code);
      }
    }

    return '';
  });

  useLayoutEffect(() => {
    if (code.length > highLightLimitSize) {
      highLightRef.current = false;
      setHighLightCode(code);
    } else {
      if (preHighlightCodeLangRef.current) {
        const { code: tempCode, lang: tempLang } = preHighlightCodeLangRef.current;
        if (tempCode === code && tempLang === lang) {
          return;
        }
      }

      highLightRef.current = true;
      // setHighLightCode(code);
      updateHighLightCode(lang, code, setHighLightCode);
      preHighlightCodeLangRef.current = {
        lang,
        code,
      };
      // highLightInWorker({ lang, code }).then((nextValue) => {
      //   highLightRef.current = true;
      //   setHighLightCode(nextValue);
      // });
    }
  }, [code, lang]);

  const [preRef, setPreRef] = useState<HTMLPreElement | null>(null);
  useLayoutEffect(() => {
    preRef?.scrollTo({
      top: 0,
      left: 0,
    });
  }, [preRef, highLightValue]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const themeClass = useMemo(() => {
    let result = 'theme-github-dark';
    if (highlightTheme) {
      return `theme-${highlightTheme}`;
    }
    switch (theme) {
      case 'lightbrown':
      case 'light':
        result = 'theme-base16-google-light';
        break;
      default:
    }
    return result;
  }, [highlightTheme, theme]);

  const currentTheme = React.useMemo(() => {
    return themeClass.includes('dark') ? 'dark' : 'light';
  }, [themeClass]);

  if (!isString(code)) {
    return null;
  }
  const codeElement = highLightRef.current ? (
    <code
      className={classNames(styles.code, 'hljs', 'hljs-' + language)}
      dangerouslySetInnerHTML={{ __html: code ? highLightValue : '' }}
    />
  ) : (
    <>{code}</>
  );

  const errorMsg = formatMessageWithLocale(locale, { id: 'overView.failToCopied' });
  const successMsg = formatMessageWithLocale(locale, { id: 'overView.isCopied' });

  if (!onlyCode) {
    const icon = (
      <>
        {!copied ? (
          <FoxIconCopyBold2
            className={styles['copy-icon']}
            onClick={(e) => {
              e.stopPropagation();
              copyTo(code || '', {
                errorMsg,
                successMsg,
              });
              setCopied(true);
              if (timerRef.current) {
                clearTimeout(timerRef.current);
              }
              timerRef.current = setTimeout(() => {
                setCopied(false);
              }, 1000);
            }}
            size={16}
          />
        ) : (
          <FoxIconCheck
            className={classNames(styles['copy-icon'], styles['check-icon'])}
            size={16}
          />
        )}
      </>
    );

    return (
      <div
        className={classNames(
          styles.code,
          'relative',
          styles.container,
          {
            [styles['container-dark']]: currentTheme === 'dark',
            [styles['container-light']]: currentTheme === 'light',
          },
          className,
        )}
      >
        <ClientOnly>
          {() => {
            return code ? icon : null;
          }}
        </ClientOnly>
        <pre
          ref={setPreRef}
          className={classNames(themeClass, styles['code-block'], 'code-block-custom')}
          style={{ '--markdown-x-component-gap': '0px' } as any}
        >
          {codeElement}
        </pre>
      </div>
    );
  }

  return (
    <pre
      ref={setPreRef}
      className={classNames(
        themeClass,
        styles['code-block'],
        'code-block-custom',
        styles.container,
        className,
      )}
      style={{ '--markdown-x-component-gap': '0px' } as any}
    >
      {codeElement}
    </pre>
  );
};

export default CodeBlock;
